import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { InteractionsOutsideVideoList } from '@/components/pages/create-webinar/config/EvergreenMp4UploadDialog';
import HmsInputs from '@/components/pages/create-webinar/evergreen/HmsInputs';
import type {
  EvergreenMp4ReplacementType,
  EvergreenScheduledInteractionType,
  EvergreenTimelineEvent,
} from '@/features/webinar/types';
import { formatFileSize } from '@/lib/evergreenMp4File';
import { hmsToSeconds, type Hms } from '@/lib/evergreenInteractionSchedule';
import {
  buildReplacementDeclaration,
  canConfirmReplacement,
  checkReplacementAgainstTimeline,
  EVERGREEN_MP4_REPLACEMENT_TYPES,
  type Mp4ReplacementDeclaration,
} from '@/lib/evergreenMp4Replacement';
import {
  applyVideoCuts,
  resolveTooShortHint,
  validateVideoCuts,
  type TooShortHint,
  type VideoCut,
  type VideoCutsDeletedItem,
} from '@/lib/evergreenVideoCuts';
import { secondsToTimeString } from '@/lib/timelineOffset';

type CutRow = { key: string; start: Hms; duration: Hms };

const EMPTY_HMS: Hms = { h: '00', m: '00', s: '00' };

const INTERACTION_LABEL_KEYS: Record<EvergreenScheduledInteractionType, string> = {
  OFFER: 'contentLiveForm.eventTypes.offer',
  SURVEY: 'contentLiveForm.eventTypes.survey',
  QUIZ: 'contentLiveForm.eventTypes.quiz',
  SHARED_RESOURCE: 'contentLiveForm.eventTypes.sharedResource',
};

const TOO_SHORT_HINT_KEYS: Record<TooShortHint, string> = {
  DECLARE_CUTS: 'sessionForm.mp4.cuts.tooShortNoCutsHint',
  CHECK_CUTS: 'sessionForm.mp4.cuts.tooShortHint',
  NEW_VIDEO: 'sessionForm.mp4.replace.tooShortHint',
};

function newCutRow(): CutRow {
  return { key: crypto.randomUUID(), start: EMPTY_HMS, duration: EMPTY_HMS };
}

function rowsToCuts(rows: CutRow[]): VideoCut[] | null {
  const cuts: VideoCut[] = [];
  for (const row of rows) {
    const startSeconds = hmsToSeconds(row.start);
    const durationSeconds = hmsToSeconds(row.duration);
    if (startSeconds == null || durationSeconds == null) return null;
    cuts.push({ startSeconds, durationSeconds });
  }
  return cuts;
}

type Props = {
  file: File;
  timeline: EvergreenTimelineEvent[];
  previousDurationSeconds: number | null;
  newDurationSeconds: number;
  webinarId: number | null;
  nextSessionLabel: string | null;
  onCancel: () => void;
  onConfirm: (replacement: Mp4ReplacementDeclaration) => void;
};

export default function ReplacementCutsDialog({
  file,
  timeline,
  previousDurationSeconds,
  newDurationSeconds,
  webinarId,
  nextSessionLabel,
  onCancel,
  onConfirm,
}: Props) {
  const { t, i18n } = useTranslation('createWebinar');
  const navigate = useNavigate();
  const [replacementType, setReplacementType] = useState<EvergreenMp4ReplacementType | null>(null);
  const [rows, setRows] = useState<CutRow[]>(() => [newCutRow()]);
  const [cutsEdited, setCutsEdited] = useState(false);

  const withCuts = replacementType === 'PREVIOUS_VIDEO_WITH_CUTS';
  const cuts = useMemo(() => (withCuts ? rowsToCuts(rows) : null), [withCuts, rows]);
  const cutsError = withCuts
    ? cuts == null
      ? 'INVALID_CUT'
      : validateVideoCuts(cuts, previousDurationSeconds, newDurationSeconds)
    : null;
  const application = useMemo(
    () =>
      cuts != null && cutsError == null
        ? applyVideoCuts(timeline, cuts, previousDurationSeconds)
        : null,
    [cuts, cutsError, timeline, previousDurationSeconds],
  );
  const check = checkReplacementAgainstTimeline(
    newDurationSeconds,
    application ? application.timeline : timeline,
  );
  const blockingInteractions = check.kind === 'blocked' ? check.interactions : null;
  const tooShortHint = resolveTooShortHint({
    blocked: blockingInteractions != null,
    replacementType,
    cutsApplied: application != null,
  });
  const declaration = buildReplacementDeclaration(replacementType, cuts);
  const canConfirm = canConfirmReplacement({
    declaration,
    check,
    cutsApplied: application != null,
  });
  const totalCutSeconds = (cuts ?? []).reduce((sum, cut) => sum + cut.durationSeconds, 0);

  const updateRow = (key: string, patch: Partial<Pick<CutRow, 'start' | 'duration'>>) => {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    setCutsEdited(true);
  };

  const describeDeleted = (item: VideoCutsDeletedItem): string => {
    switch (item.kind) {
      case 'MESSAGE':
        return t('sessionForm.mp4.cuts.deletedMessage', { text: item.text });
      case 'INTERACTION':
        return t(INTERACTION_LABEL_KEYS[item.type]);
      case 'LIVE_REACTION':
        return t('sessionForm.mp4.cuts.deletedLiveReaction');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('sessionForm.mp4.replace.confirmTitle')}</DialogTitle>
          <DialogDescription className="truncate">
            {file.name} ({formatFileSize(file.size, i18n.language)})
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <Label>{t('sessionForm.mp4.replacement.question')}</Label>
          <RadioGroup
            className="flex flex-col gap-3"
            value={replacementType ?? ''}
            onValueChange={(value) => {
              const next = EVERGREEN_MP4_REPLACEMENT_TYPES.find((type) => type === value);
              if (next) setReplacementType(next);
            }}
          >
            {EVERGREEN_MP4_REPLACEMENT_TYPES.map((type) => (
              <div key={type} className="flex items-start gap-2">
                <RadioGroupItem value={type} id={`replacement-type-${type}`} className="mt-0.5" />
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`replacement-type-${type}`}>
                    {t(`sessionForm.mp4.replacement.types.${type}`)}
                  </Label>
                  <p className="text-xs text-secondary">
                    {t(`sessionForm.mp4.replacement.hints.${type}`)}
                  </p>
                </div>
              </div>
            ))}
          </RadioGroup>
        </div>

        {withCuts && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-secondary">{t('sessionForm.mp4.cuts.hint')}</p>
            {rows.map((row) => (
              <div key={row.key} className="flex flex-col md:flex-row items-end gap-4">
                <HmsInputs
                  label={t('sessionForm.mp4.cuts.startLabel')}
                  value={row.start}
                  onChange={(start) => updateRow(row.key, { start })}
                />
                <HmsInputs
                  label={t('sessionForm.mp4.cuts.durationLabel')}
                  value={row.duration}
                  onChange={(duration) => updateRow(row.key, { duration })}
                />
                {rows.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0"
                    onClick={() => setRows((current) => current.filter((r) => r.key !== row.key))}
                    aria-label={t('sessionForm.mp4.cuts.remove')}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => setRows((current) => [...current, newCutRow()])}
              className="flex items-center gap-2"
            >
              <Plus className="size-6" />
              <Label className="cursor-pointer">{t('sessionForm.mp4.cuts.add')}</Label>
            </button>
            <p className="text-xs text-secondary">
              {t('sessionForm.mp4.cuts.durations', {
                previous:
                  previousDurationSeconds != null
                    ? secondsToTimeString(previousDurationSeconds)
                    : '-',
                next: secondsToTimeString(newDurationSeconds),
                total: secondsToTimeString(totalCutSeconds),
              })}
            </p>
            {cutsEdited && cutsError && (
              <p className="text-xs text-error">{t(`sessionForm.mp4.cuts.errors.${cutsError}`)}</p>
            )}
          </div>
        )}

        {blockingInteractions && tooShortHint ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium">{t('sessionForm.mp4.replace.tooShortTitle')}</p>
            <p className="text-sm text-secondary">{t(TOO_SHORT_HINT_KEYS[tooShortHint])}</p>
            <InteractionsOutsideVideoList interactions={blockingInteractions} />
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/create-webinar/evergreen/${webinarId}`)}
            >
              {t('sessionForm.mp4.dialog.goToInteractions')}
            </Button>
          </div>
        ) : application ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-secondary">
              {t('sessionForm.mp4.cuts.shifted', { count: application.shiftedCount })}
            </p>
            {application.deleted.length > 0 && (
              <div className="rounded-xl border border-error/40 bg-error/5 p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-error">
                  <AlertTriangle size={16} className="shrink-0" />
                  <span>{t('sessionForm.mp4.cuts.deletedTitle')}</span>
                </div>
                <ul className="max-h-48 overflow-y-auto flex flex-col gap-1">
                  {application.deleted.map((item) => (
                    <li key={item.eventId} className="flex gap-3 text-sm">
                      <span className="text-secondary w-20 shrink-0">
                        {secondsToTimeString(
                          item.kind === 'INTERACTION'
                            ? item.startOffsetSeconds
                            : item.offsetSeconds,
                        )}
                      </span>
                      <span className="text-black min-w-0 truncate">{describeDeleted(item)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          !withCuts && (
            <p className="text-sm text-secondary">{t('sessionForm.mp4.replace.confirmHint')}</p>
          )
        )}

        {nextSessionLabel && (
          <p className="text-sm text-secondary">
            {t('sessionForm.mp4.replace.nextSessionWarning', { date: nextSessionLabel })}
          </p>
        )}

        <DialogFooter className="w-full flex justify-between gap-4">
          <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
            {t('sessionForm.mp4.dialog.cancel')}
          </Button>
          <Button
            type="button"
            className="flex-1"
            disabled={!canConfirm}
            onClick={() => {
              if (canConfirm && declaration) onConfirm(declaration);
            }}
          >
            {t('sessionForm.mp4.replace.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
